import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  useTheme,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Mock data for clients and their facilities
const mockClients = [
  { id: 1, name: 'Acme Healthcare Group', contactEmail: 'contact@acme.com', contactPhone: '(555) 123-4567', facilities: 3, status: 'Active', lastLogin: '2023-10-25', subscriptionExpiry: '2024-12-31' },
  { id: 2, name: 'Regional Senior Care', contactEmail: 'info@regionalsenior.com', contactPhone: '(555) 987-6543', facilities: 2, status: 'Active', lastLogin: '2023-10-24', subscriptionExpiry: '2024-11-30' },
  { id: 3, name: 'Coastal Care Solutions', contactEmail: 'admin@coastalcare.com', contactPhone: '(555) 456-7890', facilities: 4, status: 'Active', lastLogin: '2023-10-26', subscriptionExpiry: '2025-03-31' },
  { id: 4, name: 'Mountain View Health', contactEmail: 'support@mountainview.com', contactPhone: '(555) 234-5678', facilities: 1, status: 'Inactive', lastLogin: '2023-09-15', subscriptionExpiry: '2023-09-30' },
];

// Mock data for super admin dashboard stats
const mockStats = [
  { title: 'Total Clients', value: 24, change: '+2', icon: <PeopleIcon />, color: '#1976d2' },
  { title: 'Total Facilities', value: 142, change: '+8', icon: <BusinessIcon />, color: '#7b1fa2' },
  { title: 'Active Subscriptions', value: 138, change: '+5', icon: <HomeIcon />, color: '#388e3c' },
  { title: 'Pending Support Tickets', value: 4, change: '-1', icon: <AssignmentIcon />, color: '#f57c00' },
];

const SuperAdminDashboard: React.FC = () => {
  const theme = useTheme();
  const [clients, setClients] = useState(mockClients);

  const handleViewClient = (clientId: number) => {
    // In a real app, this would navigate to the client details page
    alert(`Viewing client ${clientId}`);
  };

  const handleEditClient = (clientId: number) => {
    // In a real app, this would open an edit dialog or navigate to edit page
    alert(`Editing client ${clientId}`);
  };

  const handleDeleteClient = (clientId: number) => {
    if (window.confirm(`Are you sure you want to delete client ${clientId}? This will also delete all associated facilities. This action cannot be undone.`)) {
      setClients(clients.filter(c => c.id !== clientId));
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Super Admin Dashboard
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {mockStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: `${stat.color}20`,
                mr: 2,
              }}>
                <Avatar sx={{ bgcolor: stat.color, width: 36, height: 36 }}>
                  {stat.icon}
                </Avatar>
              </Box>
              <Box>
                <Typography variant="h4">{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                <Typography variant="caption" color="success.main">{stat.change}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      {/* Clients List */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Clients" 
              action={
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                  component={Link}
                  to="/super-admin/clients"
                >
                  Manage Clients
                </Button>
              }
            />
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact Email</TableCell>
                      <TableCell>Contact Phone</TableCell>
                      <TableCell>Facilities</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Subscription Expiry</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>
                          <Typography variant="subtitle2">{client.name}</Typography>
                        </TableCell>
                        <TableCell>{client.contactEmail}</TableCell>
                        <TableCell>{client.contactPhone}</TableCell>
                        <TableCell>{client.facilities}</TableCell>
                        <TableCell>
                          <Chip 
                            label={client.status} 
                            size="small" 
                            color={client.status === 'Active' ? 'success' : 'error'} 
                          />
                        </TableCell>
                        <TableCell>{client.lastLogin}</TableCell>
                        <TableCell>{client.subscriptionExpiry}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Client">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleViewClient(client.id)}
                              sx={{ mr: 1 }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Client">
                            <IconButton 
                              color="primary" 
                              onClick={() => handleEditClient(client.id)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Client">
                            <IconButton 
                              color="error" 
                              onClick={() => handleDeleteClient(client.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminDashboard;